import moment from 'moment-timezone'
import selectn from 'selectn'
import { common } from '../../common'
import helpers from './helpers'

const UNDEFINED = typeof void(0)

export const modules = {
  /**
   * build ordered array of article groups, sorted by game days, then previews, then other
   * @param  {Object} newsNode   news node
   * @param  {Object} edition    edition config
   * @param  {Object} params     router params
   * @param  {Object} query      router query
   * @param  {Object} hrefConfig config object for create href function
   * @return {Object} Match Coverage Groups, each with a label and array of articles stats
   */
  getMatchCoverage(newsNode = {}, edition = {}, params = {}, query = {}, hrefConfig) {
    let coverageGroups = []
    // copy articles array so sorting doesn't affect source data in case other modules depend on this data
    if (newsNode.type && newsNode.type == 'series') {
      return coverageGroups
    }
    let articles = (newsNode.articles || [])
    if (articles['constructor'] !== Array) {
      articles = []
    }
    articles = articles.concat()
    const specialGameDays = {
      MATCH_DAY : 0,
      PREVIEW : -1,
      OTHERS : -2
    }

    if(!articles.length) {
      return coverageGroups
    }

    let articlesHaveGameDay = false
    for(let i = 0; i < articles.length; i++) {
      let currArticle = articles[i] || {}
      if(typeof currArticle.gameDay === 'number') {
        articlesHaveGameDay = true
        break
      }
    }

    const getArticleGameDay = articlesHaveGameDay ? article => {
      return article.gameDay || specialGameDays.OTHERS
    } : article => {
      let articleType = (article.type || '').toLowerCase()
      return articleType === 'preview' ? specialGameDays.PREVIEW : specialGameDays.MATCH_DAY
    }

    const articleTypeWeights = {
      'preview': 1,
      'recap': 3
    }

    const getArticleTypeWeight = article => {
      return articleTypeWeights[(article.type || '').toLowerCase()] || 2
    }

    // sort by gameDay (if present), article type, published date
    articles.sort((articleA, articleB) => {
      const articleAGameDay = getArticleGameDay(articleA)
      const articleBGameDay = getArticleGameDay(articleB)

      const articleATypeWeight = getArticleTypeWeight(articleA)
      const articleBTypeWeight = getArticleTypeWeight(articleB)

      const articleAPublishedDate = (+articleA.published || -Infinity)
      const articleBPublishedDate = (+articleB.published || -Infinity)

      if(articleAGameDay === articleBGameDay) {
        if(articleATypeWeight === articleBTypeWeight) {
          return articleAPublishedDate - articleBPublishedDate
        } else {
          return articleATypeWeight < articleBTypeWeight ? 1 : -1
        }
      } else {
        return articleAGameDay < articleBGameDay ? 1 : -1
      }
    })

    const specialDayLabels = {
      '0': 'Match Day',
      '-1': 'Preview',
      '-2': 'Others'
    }

    const supportedReactLinkTypes = {
      Preview : 'preview',
      Recap : 'report'
    }

    let coverageGroup = null,
      currentGroupGameDay = NaN

    const stripTags = /(<([^>]+)>)/ig

    articles.forEach(article => {
      const articleGameDay = getArticleGameDay(article)

      if(currentGroupGameDay !== articleGameDay) {
        currentGroupGameDay = articleGameDay
        coverageGroup = {
          label: String(articleGameDay) in specialDayLabels ? specialDayLabels[String(articleGameDay)] : `Day ${articleGameDay}`,
          articles: []
        }
        coverageGroups.push(coverageGroup)
      }

      let href = selectn('links.web.href', article) || '#'
      let isReactEnabledLink = false
      const reactLinkType = supportedReactLinkTypes[article.type]

      if(reactLinkType && common.isLinkReactEnabled(href)) {
        href = common.createHref({ query, ...params, day: article.gameDay, pageType: reactLinkType, ...hrefConfig })
        isReactEnabledLink = true
      } else {
        href = params.platform ? href : common.buildURL(href, edition.domain)
      }

      coverageGroup.articles.push({ // similar to flex.buildNewsItems... do we want to consolidate?
        id: article.id,
        headline: article.headline || article.linkText || '',
        type: article.type,
        gameDay: article.gameDay,
        description: article.description || ((article.story || '').replace(stripTags, '')).substring(0, 200),
        isReactEnabledLink: isReactEnabledLink,
        href: href,
        image: selectn('images[0]', article),
        video: selectn('video[0]', article),
        published: article.published,
        author: article.author || article.byline,
        trackingName: common.createTrackingName([ params.sport, 'game', params.pageType, 'cricketnews', article.id ])
      })
    })

    return coverageGroups
  },

  getPlayersToWatch(leadersNode, gameStrip) {
    const homeTeamId = selectn('teams.home.id', gameStrip)

    const roleMap = {
      runs : 'Batsmen', wickets : 'Bowlers'
    }

    const statsMap = {
      wickets :{
        matches: { text: 'MAT', index: 0 },
        wickets: { text: 'W', index: 1 },
        economyRate: { text: 'ECO', index: 2 },
        bowlingStrikeRate: { text: 'SR', index: 3 }
      },
      runs :{
        matches: { text: 'MAT', index: 0 },
        runs: { text: 'RUNS', index: 1 },
        battingAverage: { text: 'AVG', index: 2 },
        battingStrikeRate: { text: 'SR', index: 3 }
      }
    }

    const players = common.getLeaders(leadersNode, roleMap, statsMap, homeTeamId)
    const isAvailable = (players.Batsmen || players.Bowlers) ? true : false

    return {
      Batsmen: players.Batsmen,
      Bowlers: players.Bowlers,
      tbdTeam: selectn('tbdTeam', players) || '',
      isAvailable: isAvailable
    }
  },
  /**
   * build stats data for React components
   * @param  {Array}  rosters      Rosters node
   * @param  {Object} leadersNode  leaders node
   * @param  {Object} params       router params
   * @param  {String} gameState    game state
   * @param  {Number} period       inning number
   * @param  {Object} competition  competition node
   * @param  {Object} matchFlags   flags for this match
   * @param  {Object} query        router query
   * @param  {Object} hrefConfig   config object for create href function
   * @param  {Object} scoringSource scoring source
   * @param  {Object} comparison statistics comparison
   * @return {Object}         Batsman and Bowler stats
   */
  getStatistics(rosters = [], leadersNode, params = {}, gameState, period, competition, matchFlags, query, hrefConfig, scoringSource, comparison) {

    // TODO: improve, optimize logic, add error checks
    const link = common.createHref({ query, ...params, pageType: 'statistics', ...hrefConfig })
    const trackingName = common.createTrackingName([ params.sport,'game', params.pageType, 'stats', 'morestatistics' ])
    const teamAroster = selectn('[0].roster', rosters) || []
    const teamBroster = selectn('[1].roster', rosters) || []
    const combinedRosters = teamAroster.concat(teamBroster)
    const battingStatMap = {
      runs: { text: 'R', index: 0 },
      ballsFaced: { text: 'B', index: 1 },
      fours: { text: '4S', index: 2 },
      sixes: { text: '6S', index: 3 }
    }
    const bowlingStatMap = {
      overs: { text: 'O', index: 0 },
      maidens: { text: 'M', index: 1 },
      conceded: { text: 'R', index: 2 },
      wickets: { text: 'W', index: 3 },
      economyRate: { text: 'Eco', index: 4 }
    }
    let currentPlayersIds = {
      batsmen: [],
      bowlers: []
    }
    let enabled = false

    const bestPerformersBatTitle = ((gameState != 'final') ? 'Current' : 'Best performances -') + ' Batsmen'
    const bestPerformersBowlTitle = ((gameState != 'final') ? 'Current' : 'Best performances -') + ' Bowlers'

    // Object to lookup team details of an athlete
    let athleteToTeam = helpers.getTeamOfAthletes(rosters)

    // Default object to store best players (batsmen and bowlers)
    let bestPlayers = {}
    // If gameState is fInal, Look in the leaders node for best performances
    // else pick current players
    if(gameState === 'final') {
      // Get full details of best players
      bestPlayers = helpers.getBestPlayersFromMatch(leadersNode)
      // Extract just the player IDs and their period of best play
      currentPlayersIds = helpers.getBestPlayersIds(bestPlayers)
    }else {
      currentPlayersIds = helpers.getCurrentPlayersIds(combinedRosters,period)
    }

    // Get Array of batsman and bowler ids
    const currBatsmanIds = currentPlayersIds.batsmen.map((batsman = {}) => { return batsman.playerId})
    const currBowlerIds = currentPlayersIds.bowlers.map((bowler = {}) => { return bowler.playerId})

    // Get object to lookup batsman and bowlers
    const currentBatsmen = common.getPlayersByIds(combinedRosters, currBatsmanIds) || {}
    const currentBowlers = common.getPlayersByIds(combinedRosters, currBowlerIds) || {}

    let bowlers = []
    // Iterate over current bowlers(keys are ids) (in order) and extract stats

    currentPlayersIds.bowlers.map(player => {
      // Lookup details of this bowler
      let currentPlayer = {}
      if (player.hasOwnProperty('playerId')) {
        currentPlayer = currentBowlers[player.playerId] || {}
      }
      const linescores = currentPlayer.linescores || []
      linescores.map(l => {
        const bowlingNode = selectn('statistics.bowling', l)
        // By default, flag is current period  == this line score period
        let flag = period == l.period
        /**
         * If game is completed state & playerToPeriod has this player id,
         * then flag is player's best performance was in this line score period
         */
        if (gameState === 'final') {
          flag = player.period == l.period
        }
        // Append to bowlers only if it's a bowling linescore and flag is true
        if(bowlingNode && flag) {
          const statsNode = selectn('statistics.categories[0].stats', l)
          const stats = common.filterStatsByNames(statsNode, bowlingStatMap)
          let team = selectn(`[${player}].abbreviation`, athleteToTeam) || ''
          let headshot = selectn('athlete.headshot.href', currentPlayer) || ''
          let bowlName = selectn('athlete.battingName', currentPlayer) || selectn('athlete.displayName', currentPlayer)
          headshot = headshot ? common.resizeCombinerLogo(headshot, { w:108, h:108, transparent:true, scale:'crop'  } ) : ''
          bowlers.push({
            team: team,
            img: headshot,
            name: bowlName,
            href: selectn('athlete.links[0].href', currentPlayer),
            pitchMap: {
              rhb: {
                map: selectn('statistics.bowling.pitchMapRhb', l),
                totalBalls: selectn('statistics.bowling.overallRhb.balls', l)
              },
              lhb: {
                map: selectn('statistics.bowling.pitchMapLhb', l),
                totalBalls: selectn('statistics.bowling.overallLhb.balls', l)
              }
            },
            stats: stats
          })
        }
      })
    })
    let batsmen = []
    // Iterate over current batsmen(keys are ids) (in order) and extract stats
    currentPlayersIds.batsmen.map(player => {
      // Lookup details of this batsman
      let currentPlayer = {}
      if (player.hasOwnProperty('playerId')) {
        currentPlayer = currentBatsmen[player.playerId] || {}
      }
      const linescores = currentPlayer.linescores || []

      linescores.map(l => {
        const summary = selectn('statistics.batting.preferredShot.runsSummary', l) || []
        const wagonZone = selectn('statistics.batting.wagonZone', l) || []
        const pie = wagonZone.map((zone = {}) => zone.runs)
        const battingNode = selectn('statistics.batting', l)
        // By default, flag is current period  == this line score period
        let flag = period == l.period
        /**
         * If game is completed state & playerToPeriod has this player id,
         * then flag is player's best performance was in this line score period
         */
        if (gameState === 'final') {
          flag = player.period == l.period
        }
        // Append to batsmen only if it's a batting linescore and flag is true
        if(battingNode && flag) {
          const statsNode = selectn('statistics.categories[0].stats', l)
          const stats = common.filterStatsByNames(statsNode, battingStatMap)
          let team = selectn(`[${player}].abbreviation`, athleteToTeam) || ''
          let headshot = selectn('athlete.headshot.href', currentPlayer) || ''
          let batName = selectn('athlete.battingName', currentPlayer) || selectn('athlete.displayName', currentPlayer)
          headshot = headshot ? common.resizeCombinerLogo(headshot, { w:108, h:108, transparent:true, scale:'crop'  } ) : ''
          batsmen.push({
            team: team,
            img: headshot,
            name: batName,
            href: selectn('athlete.links[0].href', currentPlayer),
            control: selectn('statistics.batting.controlPercentage', l),
            runs: `${stats[0].value}(${stats[1].value}) <span>${stats[2].value}x4 - ${stats[3].value}x6</span>`,
            pie: pie,
            productive: {
              shotType: selectn('statistics.batting.preferredShot.shotName', l),
              runs: selectn('statistics.batting.preferredShot.runs', l),
              details: `${summary[4] || 0}x4 - ${summary[6] || 0}x6`
            }
          })
        }
      })
    })

    const allPartnerships = modules.getPartnerships(matchFlags, competition)
    const partnerships = modules.getCurrentPartnerships(matchFlags, competition, period)

    // Build an array of innings (ordered by period)
    let pshipByInnings = []
    // iterate over home and away
    for (var ha in allPartnerships) {
      let side = allPartnerships[ha]
      // iterate over # innings
      for (var p in side) {
        // extract innings object
        let inn = side[p]
        let titlePresent = selectn('titles.brief', inn) !== 'undefined'
        let period = parseInt(p, 10)
        // populate array only if period is valid & title is present
        if (period >= 1 && titlePresent) {
          pshipByInnings[ period - 1 ] = { ...inn, period, ha }
        }
      }
    }

    // Set of UI controls for stats modules
    let uiControls = {
      showButtons: false,
      showTabs : false,
      showCharts: false
    }
    /**
     * Display tabs for tests and buttons for limited over matches. Show charts
     * only for limited over matches
     */
    if (matchFlags.limitedOvers) {
      uiControls.showButtons = true
      uiControls.showCharts = true
    }
    else{
      uiControls.showTabs = true
    }

    const showStatsCharts = selectn('comparison.length', comparison) !== 0 ? true : false
    if((scoringSource.isFeedback && (batsmen.length || bowlers.length || partnerships.length)) || uiControls.showCharts && showStatsCharts) {
      enabled = true
    }

    return {
      partnerships,
      allPartnerships,
      bowlers,
      batsmen,
      link,
      uiControls,
      pshipByInnings,
      enabled,
      trackingName,
      bestPerformersBatTitle,
      bestPerformersBowlTitle
    }
  },

  /**
  * Builds the data structure required to draw the table for the right hand column
  * @param  {Object} standingsNode      Raw data from the standings node returned in summary call
  * @param  {Object} params             Router params
  * @param  {Object} query              Router query
  * @param  {string} slugOfLeague       Slug of League
  * @param  {Object} hrefConfig         Config object for create href function
  * @param  {Number} year  year of the league
  * @return {Object}                    Standings data and table link
  **/
  getStandings: function ( standingsNode, params, query, slugOfLeague, hrefConfig, year ) {

    const typesToShow = [ /* stats type in order of display + special team to show teamname */
      'team',
      'matchesplayed',
      'matcheswon',
      'matcheslost',
      'matchesdraw',
      'matchpoints',
      'serieswon',
      'runsperwicketratio',
      'nrr'
    ]
    const standings     = common.getStandingsData( standingsNode, typesToShow, params.sport, 'gamepackage' )
    if(!params.leagueId) {
      params.leagueId = standingsNode.id
    }
    params = { ...params, slug : slugOfLeague }
    const standingsLink = common.getStandingsLink( params, query, '', hrefConfig, year )

    return { ...standings, link: { ...standingsLink, external:true }, params }
  },

  getMatchDetails(gameInfo, notesNode, debutsNode, featuredAthletes, params, matchFormat, league, edition) {
    let matchDetails = common.getGameDetails(gameInfo, notesNode, featuredAthletes, params )
    const debuts = helpers.getDebutants(debutsNode, params)
    /* @var {Array} series - have array just to align with other match detail items */
    let series = [ {
      name: league.name || '',
      href: common.buildURL(selectn('link.href', league), edition.domain) || '#',
      trackingName: common.createTrackingName([ params.sport, 'game', 'game', 'matchDetails' ])
    } ]
    return { ...matchDetails, debuts, matchFormat, series }
  },
  getScorecardSummary(leadersNode = [], rosters = [], allInnings = [], params = {}, query = {}, hrefConfig) {
    const teamAroster = selectn('[0].roster', rosters) || []
    const teamBroster = selectn('[1].roster', rosters) || []
    const combinedRosters = teamAroster.concat(teamBroster)

    let summary = {}
    let allInningsMapped = {}

    const fullSummaryLink = common.createHref({ query, ...params, pageType: 'scorecard', ...hrefConfig })

    summary.fullSummaryLink = fullSummaryLink

    allInnings.map((inn) => {
      const period = inn.period
      allInningsMapped[period] = inn
    })

    leadersNode.map((ln = {}) => {
      const { linescores = [] } = ln
      summary.innings = summary.innings || {}

      linescores.map((ls = {}) => {
        const { period, leaders, isBatting } = ls
        const currentInn = allInningsMapped[period]
        let leadersData
        let playersType
        let statMap
        let inningStatus
        let followOn

        const leadersId = helpers.getLeadersId(leaders)

        const players = common.getPlayersByIds(combinedRosters, leadersId)

        if (isBatting) {
          playersType = 'batsmen'
          statMap = {
            runs: { text: 'RUNS', index: 0 },
            ballsFaced: { text: 'BF', index: 1 },
            notouts: { text: 'Not Out', index: 2 }
          }
        } else {
          playersType = 'bowlers'
          statMap = {
            overs: { text: 'O', index: 0 },
            conceded: { text: 'R', index: 1 },
            wickets: { text: 'E', index: 2 }
          }
        }

        leadersData = helpers.getLeadersData(players,statMap, period)
        inningStatus = selectn('inningStatus', currentInn) || ''
        followOn = selectn('stats.brief.followOn',currentInn) || 0

        summary.innings[period] = summary.innings[period] || {}
        summary.innings[period][playersType] = leadersData

        //show empty data for forfeited and declared innings and not scored
        if(inningStatus === 'forfeit' || inningStatus === 'declared') {
          let runs = selectn('stats.brief.runs',currentInn) || '',
            overs = selectn('stats.brief.overs',currentInn)  || '',
            defaultPlayer = {
              name : '',
              href : '',
              stats : {
                runs : '',
                overs : ''
              }
            }
          if(runs === '' && overs === '') {
            summary.innings[period][playersType] = [ defaultPlayer, defaultPlayer ]
          }
        }

        if (isBatting && typeof summary.innings[period].team === UNDEFINED) {
          let runs = selectn('stats.brief.runs',currentInn) || '',
            overs = selectn('stats.brief.overs',currentInn)  || '',
            description = selectn('stats.brief.description',currentInn)  || '',
            wickets = selectn('stats.brief.wickets',currentInn) || 0

          let inningsRunWicket = runs || '0'
          if(inningStatus === 'forfeit') {
            inningsRunWicket = inningStatus
          }else{
            if(description != 'all out') {
              inningsRunWicket = inningsRunWicket + ('/' + wickets)
            }
            if(inningStatus === 'declared') {
              inningsRunWicket = inningsRunWicket + 'd'
            }
            if(followOn) {
              inningsRunWicket = inningsRunWicket + ' f/o'
            }
          }

          summary.innings[period].team = {
            teamDisplayName: (selectn('teamDisplayName',currentInn) || '').toUpperCase(),
            innDisplayName: (selectn('innDisplayName',currentInn) || '').toUpperCase(),
            runs: runs,
            overs: overs,
            wickets: wickets,
            description: description,
            inningsRunWicket : inningsRunWicket,
            inningStatus : inningStatus
          }
        }
      })
    })
    summary.isAvailable =  Object.keys(summary.innings).length ? true : false

    return summary
  },
  /**
   * get Head To Head data.
   * @param  {Object} edition   details to get domain name
   * @param  {Array}  events    events node from h2h
   * @param  {Object} teams     teams node
   * @param  {Object} h2hRef    reference team for h2h events
   * @param  {Array}  notesNode notes from the feed
   * @return {Object}           games and table keys
   */
  getHeadToHead(edition={}, events = [], teams = {}, h2hRef = {}) {
    const home = teams.home
    const away = teams.away
    const games = []
    const keys = [ {
      title: 'LAST FIVE MATCHES',
      colSpan: 7
    },{
      title: 'DATE'
    },{
      title: 'SERIES/TOURNAMENT'
    } ]
    const isAvailable = events.length ? true : false

    // Iterate over all events that these 2 teams have played before
    events.map((event) => {
      // Get team id of the team which was at home for past event
      let homeTeamId = event.homeTeamId || ''
      // Get home and away team score for past event
      let homeScore = event.homeTeamScore || ''
      let awayScore = event.awayTeamScore || ''

      //console.info('homeTeam, h2hRef', homeTeamId, h2hRef.id)

      /**
       * If home team id of this past event does not match with the reference
       * team id (home team id of current game):
       *
       * Swap the scores so that hometeamId and homeTeamScore will match in past
       * event.
       *
       * We need to swap the scores and not the team names because as per the
       * product, home team needs to be listed on left in H2H area and the
       * result of a past event is in reference of the team object of that event.
       */
      if (homeTeamId !== h2hRef.id) {
        homeScore = event.awayTeamScore || ''
        awayScore = event.homeTeamScore || ''
      }

      // Create a new object with score of the
      const homeTeam = Object.assign({}, home, { score: modules.getSplitTeamScores(homeScore) })
      const awayTeam = Object.assign({}, away, { score: modules.getSplitTeamScores(awayScore) })

      let result = ''

      switch(event.gameResult) {
        case 'W':
          result = 'beat'
          break

        case 'L':
          result = 'lost to'
          break

        case 'T':
          result = 'tied with'
          break

        case 'D':
          result = 'drew with'
          break

        case 'NR' :
          result = 'no result'
          break

        case 'ABN':
          result = 'match abandoned'
          break

        case 'PPD':
          result = 'match postponed'
          break

        case 'CNCL':
          result = 'match cancelled'
          break
      }

      /**
       * Create absolute link for the h2h game so that webview can open it in
       * minibrowser
       */
      const h2hHref = common.buildURL(selectn('links[0].href', event), edition.domain)

      games.push({
        home: (event.atVs == 'vs') ? homeTeam : awayTeam,
        away: (event.atVs == 'vs') ? awayTeam : homeTeam,
        result: result,
        // TODO: format date
        date: moment(event.gameDate).format('DD/MM/YY'),
        leagueName: event.leagueName,
        url: h2hHref || '#'
      })
    })

    return { keys, games, isAvailable }
  },

  /**
    * Get string and and check for 'ov' or 'target'
    * @param {String} str score string
    * @return {Boolean}  containOverOrTarget true if string contains 'ov' or 'target' else false
    */
  isOverOrTarget(str) {
    const containOverOrTarget = str.indexOf('ov') !== -1 || str.indexOf('target') !== -1
    return containOverOrTarget
  },

  /**
    * Get team score string and remove overs and targets wrapped inside brackets "()"
    * Split second team score to new <span /> element after "&".
    * @param {String}  score  combined first & second innings score of a team
    * @return {String}  teamScore  formated team score
    */
  getSplitTeamScores(score) {
    // default return value
    let teamScore = score
    const delimiter = '&'
    // Proceed only if score contains overs or target identified by 'ov' or 'target'
    if(modules.isOverOrTarget(teamScore)) {
      // split score at '(' to a array
      let teamScoreArr = teamScore.split('(')
      // ittrate over split score array to get overs and target string from score
      for (var i = 1; i < teamScoreArr.length; i++) {
        const iOvrTrgt = teamScoreArr[i]
        // Proceed only if score contains overs or target
        if(modules.isOverOrTarget(iOvrTrgt)) {
          // split iOvrTrgt string at ')' and re-wrap the ov/target with '()'
          const ovrTrgtStr = '('+iOvrTrgt.split(')')[0]+')'
          // Replace overs or target inside () in team score with empty string
          teamScore = teamScore.replace(ovrTrgtStr, '')
        }
      }
    }

    // Proceed only if score contains '2nd inning score' identified by '&'.
    if (teamScore.indexOf(delimiter) !== -1) {
      // split the score at 'demiliter'
      const scores = teamScore.split(delimiter)
      // assign 2nd inning score to new <span />
      teamScore = `${scores[0]}${delimiter}<span class='break'>${scores[1]}</span>`
    }
    return teamScore
  },

  /**
   * Get heading/title for match notes in during a limited over live game.
   * This is a workaround since during a live limited over game, match notes
   * do not have innings title. But in post game state, they have the innings
   * title.
   *
   * @param {String}  currentNum  current number of the note (section number/daynumber)
   * @param {Object}  matchFlags  flags for this match
   * @param {String}  gameState   state of match
   * @param {Array}   allInnings  all Innings for this match
   * @return {String}             heading for notes if found
   */
  getNoteHeadingForLiveGame(currentNum = '', matchFlags = {}, gameState = '', allInnings = []) {
    // default return value
    let heading = ''
    //console.info('matchFlags, gameState', matchFlags, gameState)

    // Proceed only if it's a limited over match in live state
    if (matchFlags.limitedOvers === true && gameState === 'live') {
      //console.info('currentNum', currentNum, typeof currentNum)

      // Procced only if current section number is 1/2
      if ([ '1','2' ].indexOf(currentNum) != -1) {
        // Used for looking up innings details which is 0 indexed array
        let innIndex = currentNum - 1
        // Get innings title
        let innTitle = selectn(`[${innIndex}].title`, allInnings) || ''
        if (innTitle !== '') {
          heading = innTitle
        }
        //console.info('currentNum, innTitle', currentNum, innTitle)
      }
    }
    return heading
  },


  /**
   * Categorize match notes into Arrays based on days/innings.
   * For test matches, notes will be grouped by days. If day is 0, it should be
   * grouped under General.
   * For limited over matches, notes will be grouped by team name Innigs
   *
   * @param  {Array}  notesNode   Notes node from API
   * @param {Object}  matchFlags  flags for this match
   * @param {String}  gameState   state of match
   * @param {Array}   allInnings  all Innings for this match
   * @return {Array}            Notes for this match grouped by days/innings
   */
  getMatchNotes(notesNode = [], matchFlags = {}, gameState, allInnings) {
    // default return data
    let noteGroups = []
    //console.info('matchFlags', matchFlags)
    /**
     * Contain title and notes for a group (day of a test match or innings of
     * limited over match)
     * @type {Object}
     */
    let group = {}
    // Number of the group. e.g. 1/2/3/4/5 for test or 1/2 for ODI
    let groupNum
    // Flag to start getting data for next group
    let isNewGroup
    // Current number of the note
    let currentNum
    // Title of the group
    let title

    // Notes are identified by which key - dayNumber for test, section for ODI
    let lookup = matchFlags.limitedOvers === true ? 'section' : 'dayNumber'

    let numNotes = notesNode.length
    // Iterate over all match notes and group them day or innings wise
    for (var i = 0; i < numNotes; i++) {
      let note = notesNode[i] || {}
      /**
       * Continue to next note if note is not a matchnote or
       * note does not have section/dayNumber key
       */
      if (note.type !== 'matchnote' || note.hasOwnProperty(lookup) === false) {
        continue
      }

      // Get number of this note
      currentNum = note[lookup]
      // Update flag if current Number is not equal to existing group number
      isNewGroup = currentNum != groupNum

      // For ODIS, title is Team Name Innings
      if (matchFlags.limitedOvers === true) {
        title =  `${note.text}`
      }
      // For test matches, title is either Day N or General (if day is 0)
      else{
        title = currentNum == 0 ? 'General' : `Day ${currentNum}`
      }

      //console.info('isNewGroup, currentNum, title', isNewGroup, currentNum, title)

      // If note is for a new group, update groupNum, reset group & push to noteGroups
      if (isNewGroup) {
        let heading = modules.getNoteHeadingForLiveGame(currentNum, matchFlags, gameState, allInnings)
        //console.info('heading', heading)
        title = heading !== '' ? heading : title
        groupNum = currentNum
        group = {
          title : title,
          notes: []
        }
        noteGroups.push(group)
      }

      /**
       * Make sure that section heading and note title are not same before
       * inserting note in notes array
       */
      let noteText = note.text || ''
      let groupTitle = group.title || ''
      let isNoteHeadingDifferent
      try{
        isNoteHeadingDifferent = noteText.toLowerCase() != groupTitle.toLowerCase()
      }
      catch(e) {
        isNoteHeadingDifferent = true
      }


      // Push current note details to notes array
      if (note.hasOwnProperty(lookup) && group.hasOwnProperty('notes') && isNoteHeadingDifferent) {
        group.notes.push({
          id: note.id,
          day: note[lookup],
          text: note.text
        })
      }
    }

    // Iterate over note groups and set expanded flag on each group
    let numNotegroups = noteGroups.length
    for (let j = 0; j < numNotegroups; j++) {
      let g = noteGroups[j] || {}
      /**
       * If the group is last element in array, set expanded to true
       * i.e. We always expand notes of the last section of match notes
       */
      g.expanded = numNotegroups === (j+1) ? true : false
    }

    // Reverse display of match notes for test matches
    if (matchFlags.limitedOvers === false && noteGroups.length > 1) {
      noteGroups.reverse()
    }
    //console.info('noteGroups', noteGroups)

    return { noteGroups }
  },


  /**
   * For multiday matches, supposed to be avaialanle in the API at the end of the day
   *
   * @param  {Array}  notesNode   Notes node from API, type `closeofplay`
   * @return {Array}  cop for this match
   */
  getCloseOfPlay(notesNode = []) {
    // default return data
    let cop = []

    for (var i = 0; i < notesNode.length; i++) {
      let note = notesNode[i] || {}

      if (note.type === 'closeofplay') {
        cop.push({
          date: note.date || '',
          text: note.text || ''
        })
      }
    }

    return { cop }
  },


  /**
   * build links for tertiary nav
   * TODO: Add logic to hide/show links based on available data
   * @param  {Object} params        router params
   * @param  {Object} query         router query
   * @param  {Object} hidden        links to be displayed
   * @param  {String} gameState     game state
   * @param  {string} slugOfLeague  Slug of League
   * @param  {Object} hrefConfig    Config object for create href function
   * @param  {Number} year  year of the league
   * @return {Array}                game package links
   */
  buildNav(params = {}, query = {}, hidden = {}, gameState, slugOfLeague, hrefConfig = {}, year) {
    const linksToShow = {
      pregame: [ 'game', 'preview', 'commentary', 'video', 'coverage', 'table', 'hawkeye' ],
      live: [ 'game', 'scorecard', 'report', 'commentary', 'video', 'coverage', 'statistics', 'table', 'hawkeye' ],
      final: [ 'game', 'scorecard', 'report', 'commentary', 'video', 'coverage', 'statistics', 'table', 'hawkeye' ]
    }
    const linkDescriptors = {
      game: {
        text: 'Summary'
      },
      scorecard: {
        text: 'Scorecard'
      },
      commentary: {
        text: 'Commentary'
      },
      statistics: {
        text: 'Statistics'
      },
      coverage: {
        text: 'Coverage'
      },
      video: {
        text: 'Videos'
      },
      table: {
        text: 'Table',
        external: 'standings'
      },
      preview: {
        text: 'Preview'
      },
      report: {
        text: 'Report'
      },
      hawkeye: {
        text : 'Hawk-Eye'
      }
    }
    const links = [];

    (linksToShow[gameState] || []).map(link => {
      if(!hidden[link]) {
        const details = linkDescriptors[link]

        // skip if details not available
        if (typeof details === UNDEFINED) {
          return
        }

        let hrefArgs = { query, ...params, pageType: link, day: '', ...hrefConfig }

        if (link === 'table') {
          hrefArgs.slug = slugOfLeague
          hrefArgs.season = year
        }

        const text = details.text
        const linkDetails = {
          text: link === 'game' && gameState === 'live' ? 'Live' : text,
          pageType: link,
          href: common.createHref(hrefArgs),
          trackingName:common.createTrackingName([ params.sport, 'game', 'subnav', link ])
        }
        if(details.external) {
          linkDetails.external = details.external
          linkDetails.trackingName = `&lpos=${params.sport}:game:${link}::fullstandings`
        }
        links.push(linkDetails)

      }
    })

    return links
  },
  /**
   * get rosters, the method considers input from
   * both `squads` node and `rosters` node
   * @param  {Array}  squadNode   squad node
   * @param  {Array}  rostersNode rosters node
   * @param  {Object} teams       teams object
   * @param  {Array}  notesNode   notes from the feed
   * @return {Object}             rosters
   */
  getRosters(squadNode = [], rostersNode = [], teams = {}, notesNode=[]) {
    let sourceNode
    const rosters = {}
    let tossInfo = common.getNote(notesNode, 'toss') || ''

    if(helpers.isRosterAvailable(rostersNode)) {
      sourceNode = rostersNode
    }else{
      sourceNode = squadNode || []
    }

    sourceNode.map((team) => {
      let homeAway
      let currentTeamId = selectn('team.id', team)
      let homeTeamId = selectn('home.id', teams)
      let awayTeamId = selectn('away.id', teams)

      if(currentTeamId == homeTeamId) {
        homeAway = 'home'
      }else if(currentTeamId == awayTeamId) {
        homeAway = 'away'
      }
      // Proceed only if teams.home/away.id property exists
      if (typeof selectn(`[${homeAway}].id`, teams) !== 'undefined') {
        rosters[homeAway] = rosters[homeAway] || {}
        rosters[homeAway].name =  teams[homeAway].name
        rosters[homeAway].shortName =  teams[homeAway].shortName
        rosters[homeAway].abbrev =  teams[homeAway].abbrev
        rosters[homeAway].href =  teams[homeAway].href
        rosters[homeAway].logo =  teams[homeAway].logo
        rosters[homeAway].uid =  teams[homeAway].uid
        rosters[homeAway].isDisabledTab = teams[homeAway].name === 'TBD' ? true : teams[homeAway].name === 'TBA' ? true : false

        const athletes = team.roster || team.athletes || []

        const players = (athletes).map((player) => {
          let playerRoles = helpers.getPlayerRoles(player)
          // Looks for links array, to extract profile url
          const playerNode = player.links && player.links.length ? player : player.athlete
          let name = selectn('athlete.displayName', player) || ''
          name = player.displayName || player.name || name
          let role = selectn('position.name', player)
          const athleteLinkText = helpers.getAthleteLinkText(playerNode)
          return {
            id: player.id,
            name: `${name} ${playerRoles}`,
            role: role === 'Unknown' ? '' : role,
            href: common.extractUrl(playerNode, athleteLinkText, 'desktop')
          }
        })
        rosters[homeAway].players = players
      }

    })
    let homePlayers = (rosters.home && rosters.home.players) ? rosters.home.players.length : 0
    let awayPlayers = (rosters.away && rosters.away.players) ? rosters.away.players.length : 0
    let isAvailable = homePlayers || awayPlayers || 0
    rosters.isAvailable = isAvailable === 0 ? false : true
    rosters.isPlayingXI = tossInfo && homePlayers === 11 && awayPlayers === 11 ? true : false
    return rosters
  },

  /**
   * Get details of bowlers for End Of Over module
   * @param  {Object} item node from items in commentary/details
   * @return {Array}      bowlers at the end of over
   */
  getEodBowlers: function ( item = {} ) {
    // Order in which bowlers needs to be iterated over
    let overBowlers = [ 'bowler' , 'otherBowler' ]
    // default return data
    let bowlers = []

    // Iterate over bowlers, extract details and populate bowlers array
    for (var j = 0; j < overBowlers.length; j++) {
      // bowler / other bowler
      let currentBowler = overBowlers[j]
      // Get player id
      let playerId = selectn(`${currentBowler}.athlete.id`, item)

      // Process stats only if key is found and player id is present
      if (item.hasOwnProperty(currentBowler) && typeof playerId !== 'undefined') {
        // player data
        let d = item[currentBowler] || {}
        let playerName = selectn('athlete.displayName', d)
        // bowler stats which need to be picked
        let pickStats = [ 'overs', 'maidens', 'conceded', 'wickets' ]
        let stats = []
        // iterater over pickStats and populate stats array
        for (var k = 0; k < pickStats.length; k++) {
          let currentStat = pickStats[k]
          if (d.hasOwnProperty(currentStat)) {
            stats.push(d[currentStat])
          }
        }
        // create stats string
        let playerStats = stats.join('-')
        // push to current player to bowlers array
        bowlers.push({
          name: playerName,
          stat: playerStats
        })
      }
    }
    //console.info('bowlers', bowlers)
    return bowlers
  },

  /**
   * Get details of batsmen for End Of Over module
   * @param  {Object} item node from items in commentary/details
   * @return {Array}      batsmen at the end of over
   */
  getEodBatsmen: function ( item = {} ) {
    // Order in which batsmen needs to be iterated over
    let overBatsmen = [ 'batsman', 'otherBatsman' ]
    // default return data
    let batsmen = []
    let batsmenOutId = ''
    const dismissal = selectn('dismissal.dismissal', item) ? true : false
    // check if the batsman out based on above dismissal
    if(dismissal) {
      batsmenOutId = selectn('dismissal.batsman.athlete.id', item) || ''
    }

    // Iterate over batsmen, extract details and populate batsmen array
    for (var j = 0; j < overBatsmen.length; j++) {
      // batsman / otherBatsman
      let currentBatsman = overBatsmen[j]
      // Get player id
      let playerId = selectn(`${currentBatsman}.athlete.id`, item)

      // Process stats only if key is found and player id is present
      if (item.hasOwnProperty(currentBatsman) && typeof playerId !== 'undefined' && playerId !== batsmenOutId) {
        // player data
        let d = item[currentBatsman] || {}
        let playerName = selectn('athlete.displayName', d)
        // bowler stats which need to be picked
        let pickStats = [ 'totalRuns', 'faced' /*,'fours', 'sixes'*/ ]
        let stats = []
        // iterater over pickStats and populate stats array
        for (var k = 0; k < pickStats.length; k++) {
          let currentStat = pickStats[k]
          if (d.hasOwnProperty(currentStat)) {
            let statValue = d[currentStat]
            if (currentStat === 'faced') {
              statValue = `(${statValue}b)`
            }
            stats.push(statValue)
          }
        }
        // create stats string
        let playerStats = stats.join(' ')
        // push to current player to batsmen array
        batsmen.push({
          name: playerName,
          stat: playerStats
        })
      }
    }
    //console.info('batsmen', batsmen)
    return batsmen
  },

  /**
   * Get Score details at the end of over
   * @param  {Object} item node from items in commentary/details
   * @param  {Object} over over object from item
   * @param  {Object} isTiebreaker  flag if tie-breaker innings
   * @return {String} over number | Runs Wkts| inningsScore
   */
  getEodScore: function (item = {}, over = {}, isTiebreaker = false) {
    // Get runs, over, wickets in this over
    let overNumber = typeof over.number === 'undefined' ? '' : over.number
    let overRuns = typeof over.runs === 'undefined' ? '' : over.runs
    let overWickets = typeof over.wickets === 'undefined' ? '' : over.wickets
    let team = item.team || {}
    let teamName = typeof team.abbreviation === 'undefined' ? ( team.displayName || '' ) : team.abbreviation
    // default return value
    let endOfOverDisplayable = ''
    // Since over number is 0, hide end of over
    if (overNumber === 0) {
      return endOfOverDisplayable
    }

    // get score of the team from innings node (runs/wickets)
    let runs = selectn('innings.runs', item)
    let wickets = selectn('innings.wickets', item)
    let overScore = ''
    if(typeof runs != 'undefined' && typeof wickets != 'undefined' && runs !== '' && wickets !== '') {
      overScore = `${ runs }/${ wickets}`
    }
    /*
    console.info('item', item)
    console.info('overNumber %s, overRuns %s, overWickets %s, overScore %s',
      overNumber, overRuns, overWickets, overScore)
    */

    /**
     * Array to hold end of over data:
     * over number, runs scored, wickets fallen, innings score
     * @type {Array}
     */
    let endOfOver = []

    if (overNumber !== '' && overNumber != 0) {
      endOfOver.push(overNumber)
    }

    // default value for runs and wickets info
    let runsWickets = ''

    // Check if this over is maiden
    if (overRuns === 0) {
      runsWickets = '(maiden)'
      if (overWickets > 0) {
        runsWickets = '(wicket maiden)'
      }
    }
    // Check If over is not maiden
    else{
      if (overRuns !== '') {
        runsWickets = `${overRuns} ${ overRuns == 1 ? 'Run' : 'Runs'}`
      }
      if (overWickets != 0) {
        runsWickets += ` ${overWickets} ${ overWickets == 1 ? 'Wkt' : 'Wkts'}`
      }
    }

    if (isTiebreaker) {
      runsWickets = ''
    }

    if (runsWickets !== '') {
      endOfOver.push(runsWickets)
    }
    if (overScore !== '') {
      // Add team name only if it is not empty
      if(teamName !== '') {
        endOfOver.push(`${teamName}: ${overScore}`)
      }
      else {
        endOfOver.push(overScore)
      }
    }
    // Join only if array is not empty
    if (endOfOver.length !== 0) {
      endOfOverDisplayable = endOfOver.join(' | ')
    }
    return endOfOverDisplayable
  },

  /**
   * Get x runs required from y balls/overs, RR: x.xx, RRR: y.yy. Only for limited overs matches (handled in the jsx)
   * @param  {Object} item node from items in commentary/details
   * @param  {Object} over over object from item
   * @return {String} over number | Runs Wkts| inningsScore
   */
  getEodRunsReq: function (item = {}) {

    // default return value
    let endOfOverRunsReqDisplayable = ''

    let remainingOverOrBalls = 0

    // get innings number, balls/overs remaining, current run rate, required run rate from innings node
    let inningsNumber = selectn('innings.number', item)
    let remainingBalls = selectn('innings.remainingBalls', item)
    let remainingOvers = selectn('innings.remainingOvers', item)
    let currentRunRate = selectn('innings.runRate', item)
    let requiredRunRate = selectn('innings.requiredRunRate', item)
    let requiredRuns = selectn('innings.remainingRuns', item) ? selectn('innings.remainingRuns', item) : 0

    /*console.info('inningsNumber %s, requiredRuns %s, remainingOvers %s, remainingBalls %s, currentRunRate %s, requiredRunRate %s',
      inningsNumber, requiredRuns, remainingOvers, remainingBalls, currentRunRate, requiredRunRate)*/

    //Overs or Ball/Balls remaining
    if(remainingOvers > 10) {
      remainingOverOrBalls = ' from ' + remainingOvers + ' overs'
    }else if (remainingBalls > 0) {
      remainingOverOrBalls = ' from ' + remainingBalls + (remainingBalls > 1 ? ' balls' : ' ball')
    }

    /**
     * Array to hold end of over data:
     * runs required, from overs or balls, current run rate, required run rate
     * @type {Array}
     */
    let endOfOverRunsReq = []

    //current run rate for 1st innings
    if(inningsNumber && inningsNumber === 1) {
      if(currentRunRate) {
        endOfOverRunsReq.push('| RR: ' + currentRunRate.toFixed(2))
      }
    }

    //runs required from y balls/overs, RR: x.xx, RRR: y.yy for 2nd Innings
    if(inningsNumber && inningsNumber === 2) {
      if(requiredRuns) {
        endOfOverRunsReq.push(requiredRuns + (requiredRuns > 1 ? ' runs' : ' run') + ' required')
      }
      if(requiredRuns > 0 && remainingOverOrBalls) {
        endOfOverRunsReq.push(remainingOverOrBalls)
      }
      if(currentRunRate) {
        endOfOverRunsReq.push((requiredRuns > 0 ? ', RR: ' : '| RR: ') + currentRunRate.toFixed(2))
      }
      if(requiredRunRate) {
        endOfOverRunsReq.push(', RRR: ' + parseFloat(requiredRunRate).toFixed(2))
      }
    }

    // Join only if array is not empty
    if (endOfOverRunsReq.length !== 0) {
      endOfOverRunsReqDisplayable = (inningsNumber === 2 && requiredRuns > 0) ? '(' + endOfOverRunsReq.join('') + ')' : endOfOverRunsReq.join('')
    }

    return endOfOverRunsReqDisplayable
  },

  /**
   * get commentary
   * @param  {Array}   items             items node
   * @param  {Array}   tiebreaker        super over node
   * @param  {Object}  params            router params
   * @param  {Object}  query             router query
   * @param  {String}  scorer            scorer of the match
   * @param  {String}  commentator       commentator of the match
   * @param  {String}  gameState         state of the match
   * @param  {Boolean} isPartcomms       is partial commentary requested
   * @param  {String}  presentLocalTime  present local time
   * @param  {Number}  maxBalls          maximum number of balls to show in the commentary
   * @param  {Object}  hrefConfig        Config object for create href function
   * @param  {Boolean} updatesOnly       matchFlags updatesOnly
   * @return {Object}        commentary
   */
  getCommentary(items = [], tiebreaker = [], params = {}, query = {}, scorer = '', commentator = '', gameState = '', isPartcomms = false, presentLocalTime='', maxBalls, hrefConfig, updatesOnly = false) {
    /**
     * If game state is final, show only 6 balls in the
     * partial commentary module on summary tab.
     *
     */
    if (gameState === 'final' && isPartcomms === true && items instanceof Array === true) {
      items = items.slice(0, 6)
    }

    // Display required number of balls in commentary
    if(typeof maxBalls == 'number') {
      items = items.slice(0, maxBalls)
    }

    const isTiebreaker = tiebreaker.length > 0 ? true : false

    // prepend super over commentary (show full super over even if match is over)
    items = tiebreaker.concat(items)

    /**
     * Display ground time only when game state is not final and module is
     * partial commentary.
     * @type {String}
     */
    let groundTime = ''
    if (gameState !== 'final' && isPartcomms === true) {
      groundTime = presentLocalTime
    }

    let comments = []
    let overs = []
    let overDiscrete = {}
    let currentOver = -1
    let currentInn  = -1
    let enabled = false
    let commentPreText = ''
    const filterActive = query.filter
    const showFullCommentary = !filterActive || filterActive.toUpperCase() === 'FULL'
    const isHighlight = filterActive && filterActive.toUpperCase() === 'HIGHLIGHT'
    const href = common.createHref({ query, ...params, pageType: 'commentary', ...hrefConfig })
    const trackingName = common.createTrackingName([ params.sport, 'game', params.pageType, 'fullcommentary' ])

    items.map((item = {}) => {

      //console.info('item', item)
      // if over changed reset comments and update currentOver
      if(currentOver !== item.over.number || (isTiebreaker && currentInn !== item.innings.number)) {

        currentOver = item.over.number

        if (isTiebreaker) {
          currentInn = item.innings.number
        }

        comments = []
        overDiscrete = {
          comments,
          overNo: currentOver,
          completed: item.over.complete || false
        }
        // append the discrete over to overs list
        overs.push(overDiscrete)
      }

      if (commentPreText === '' && showFullCommentary) {
        commentPreText = item.preText || ''
      }

      const over = item.over || {}
      let commsText = item.text || ''
      // default full text to commentary
      let fullText = commsText
      let event = helpers.getBallValue(item)
      let shortText = item.shortText || ''
      let dismissalText = ''
      if(selectn('dismissal.dismissal', item) == true || selectn('dismissal.retiredText', item) == 'hurt') {
        dismissalText = selectn('dismissal.text', item) || ''
      }
      if(event == 4 || event == 6 || event == 'W') {
        let regex = /\b(FOUR|SIX|OUT|four|six|out)\b/g
        shortText = shortText.replace(regex, '<b>$1</b>')
      }
      // Check if bowler to batsman, event test is available
      if (shortText !== '') {
        fullText = [ shortText, commsText ].join(', ')
        //console.info('fullText', fullText)
      }


      let currentComment = {
        timeStamp : over.actual,
        text : helpers.removeScriptTag(fullText),
        event : event,
        commId: item.id,
        commSeq : item.sequence,
        dismissalText
      }

      if (showFullCommentary || isHighlight) {
        currentComment.pre = helpers.removeScriptTag(item.preText)
        currentComment.post = helpers.removeScriptTag(item.postText)
      }

      // Check if current commentary has valid video id associated with it
      const hasVideoId = typeof selectn('media.id', item) !== 'undefined' ? true : false
      currentComment.hasVideoId = hasVideoId
      // Set video object for current commentary
      if (hasVideoId === true) {
        let video = item.media || {}
        currentComment.video = video
        currentComment.videoId = video.id || ''
        //console.info('video', video)
      }

      comments.push(currentComment)

      // flag to check if this over is complete
      let isOverComplete = over.complete == true ? true : false
      //let isOverBeingBowled = over.number > 0 ? true : false

      // if over is complete, parse end-of-over
      if(isOverComplete && showFullCommentary) {
        Object.assign(overDiscrete, {
          endOfOver: modules.getEodScore(item, over, isTiebreaker),
          endOfOverRunsReq: modules.getEodRunsReq(item, over)
        })

        if (isTiebreaker === false) {
          Object.assign(overDiscrete, {
            batsmen: modules.getEodBatsmen(item),
            bowlers: modules.getEodBowlers(item)
          })
        }
      }
      // condition added if No result (abandoned with a toss) - need to show comment pre text
      if(!updatesOnly && (overs.length || (overs.length === 0 && commentPreText.length > 0))) {
        enabled = true
      }

    })
    return { overs, href, enabled, scorer, commentator, commentPreText, trackingName, groundTime, currentOver }
  },

  /**
   * [getLecClips Get LEC clips into array like allVideos]
   * @param  {[type]} items [data from fullcommentary]
   * @return {[type]}       [retursn array]
   */
  getLecClips(items) {
    let lec_arr = []
    items.map((vid) => {
      let item = vid.media
      if(item) {
        if(item.id) {
          lec_arr.push(item)
        //console.info('lec item',item)
        }
      }
    })
    return lec_arr
  },

  /**
   * Get videos associated with each player from the roster
   * @param  {Array}  rostersNode rosters for a match
   * @return {Array}              video objects associated with each player
   */
  getRosterVideos(rostersNode = []) {
    // default return value
    let rosterVideos = []
    //console.info('rostersNode', rostersNode)
    /**
     * Iterate over roster of each team. Within each team, iterate over all
     * players, If a player has video associated with it, append to rosterVideos
     */
    for (let i = 0; i < rostersNode.length; i++) {
      // get current team
      let currentTeam = rostersNode[i] || {}
      // get roster of current team
      let roster = currentTeam.roster || []
      let rosterLen = roster.length || 0
      // iterate over all players from this team
      for (let j = 0; j < rosterLen; j++) {
        let playerRoster = roster[j] || {}
        const playerLinescores = playerRoster.linescores || []
        let playerLinescoresLen = playerLinescores.length || 0
        for (let k = 0; k < playerLinescoresLen; k++) {
          let playerLinescore = playerLinescores[k] || {}
          let playerMediaObject = playerLinescore.media || {}
          if (typeof playerMediaObject !== 'undefined') {
            rosterVideos.push(playerMediaObject)
          }
        }
      }
    }
    return rosterVideos
  },

  /**
   * get Partnerships
   * @param {Object} matchFlags flags for this match
   * @param  {Object} competition  competition node
   * @param  {Function} filter     callback to filter innings
   * @return {Array|Object}        Partnerships
   */
  getPartnerships(matchFlags = {}, competition = {}, filter = () => 0) {
    let partnerships = {}
    let filteredPartnerships = []
    const competitors = competition.competitors || []
    //console.info('competition', competition)
    competitors.map((competitor = {}) => {
      //console.info('competitor', competitor)
      const linescores = competitor.linescores || []
      const homeAway = competitor.homeAway

      //console.info('team %s homeAway %s', teamName, homeAway)
      partnerships[homeAway] = partnerships[homeAway] || {}

      linescores.map((score = {}) => {
        const period = score.period
        const partnershipsNode = score.partnerships || []

        let titles = helpers.getInningsTitle(competitor, matchFlags, score)
        //partnerships[homeAway].title = inningTitle
        partnershipsNode.map((partnership = {}) => {
          let notOut = ''
          const playerAfaced = selectn('batsmen[0].balls', partnership)
          const playerAruns = selectn('batsmen[0].runs', partnership)
          const playerBfaced = selectn('batsmen[1].balls', partnership)
          const playerBruns = selectn('batsmen[1].runs', partnership)
          if(partnership && partnership.fowType != 'out') {
            notOut = '*'
          }
          // Get both player's run(balls faced) value for a partnership
          let playerAStat = playerAruns
          if (typeof playerAfaced !== 'undefined') {
            playerAStat = `${playerAruns} (${playerAfaced})`
          }
          let playerBStat = playerBruns
          if (typeof playerBfaced !== 'undefined') {
            playerBStat = `${playerBruns} (${playerBfaced})`
          }

          const ply1Name = selectn('batsmen[0].athlete.battingName', partnership) || selectn('batsmen[0].athlete.displayName', partnership)
          const ply2Name = selectn('batsmen[1].athlete.battingName', partnership) || selectn('batsmen[1].athlete.displayName', partnership)
          const formattedPartnership = {
            total: partnership.runs + notOut,
            playerA: {
              name: ply1Name,
              value: playerAStat,
              runs: playerAruns
              // TODO: calc and pass percentage for bar widths
            },
            playerB: {
              name: ply2Name,
              value: playerBStat,
              runs: playerBruns
              // TODO: calc and pass percentage for bar widths
            }
          }

          if(score.partnerships) {
            if(filter(score)) {
              // Only append if runs for either player is available
              if(playerAruns || playerBruns) {
                filteredPartnerships.push(formattedPartnership)
              }
            }else {
              // Init home/away.1/2 to an empty object
              partnerships[homeAway][period] = partnerships[homeAway][period] || {}
              // Init home/away.1/2.data to an empty array - This will hold actual formattedPartnership objects
              partnerships[homeAway][period].data = partnerships[homeAway][period].data || []
              //console.info('partnerships[homeAway][period].data', partnerships[homeAway][period].data)
              // Append each partnership in that period to data array
              // Only append if runs for either player is available
              if(typeof playerAruns !== 'undefined' || typeof playerBruns !== 'undefined') {
                //console.info('formattedPartnership', formattedPartnership)
                partnerships[homeAway][period].data.push(formattedPartnership)
              }
              // Set title of home/away.1/2 innings
              partnerships[homeAway][period].titles = titles
            }
          }

        })

      })
    })
    //console.info('partnerships', partnerships)
    return filteredPartnerships.length ? filteredPartnerships : partnerships
  },
  /**
   * get current inning partnerships
   * @param {Object} matchFlags flags for this match
   * @param  {Object} competition competition node
   * @param  {Number} period      inning number and period
   * @return {Array|Object}       partnerships
   */
  getCurrentPartnerships(matchFlags ,competition = {}, period) {
    return modules.getPartnerships(matchFlags, competition, (score) => score.period == period)
  },

  getSliderVideos(article, videosNode, params) {
    let articleVideos = article.video || []
    let articleVidIds = []
    let videos = []
    let highlightVideos = []
    let otherVideos = []
    const HIGHLIGHT = 'highlight'
    videosNode = (!videosNode.length) ? [] : videosNode

    articleVidIds = articleVideos.map(v => v.id)

    articleVideos.map((video) => {
      let coverageType = selectn('tracking.coverageType', video) || ''
      coverageType = coverageType.toLowerCase()

      if(coverageType === HIGHLIGHT) {
        highlightVideos.push(video)
      } else {
        otherVideos.push(video)
      }
    })

    videosNode.map((video) => {
      let coverageType = selectn('tracking.coverageType', video) || ''
      coverageType = coverageType.toLowerCase()

      if(articleVidIds.indexOf(video.id) == -1) {
        if(coverageType === HIGHLIGHT) {
          highlightVideos.push(video)
        } else {
          otherVideos.push(video)
        }
      }
    })

    videos = highlightVideos.concat(otherVideos)

    return {
      articleVideos: articleVideos,
      highlightVideos: highlightVideos,
      videos: videos,
      hasMoreVideosTab: (videosNode.length > 0) ? true : false,
      image: selectn('images[0]', article),
      href: common.createHref({ ...params, pageType: 'video' })
    }
  },
  getAllVideos(videosNode) {
    const videos = common.getVideos(videosNode)
    const hasMore = videos.length > 1 ? true : false
    const tabs = {
      all: { displayValue: 'All Videos', type : 'all' },
      highlight: { displayValue: 'Match Highlights', type : 'highlight' }
    }

    return { videos, hasMore, tabs }
  },
  getComparisons(competition = {}, matchFlags = {}) {
    const competitors = competition.competitors || []
    let parsed = { home:{ 1:{}, 2:{} }, away:{ 1:{}, 2:{} } }

    // default return data
    const comparison = []
    // Charts should not appear for tests so no need to parse data
    if (matchFlags.limitedOvers === false) {
      return { comparison }
    }

    competitors.map(competitor => {
      const teamName = selectn('team.displayName', competitor) || ''
      const teamAbbr = selectn('team.abbreviation', competitor) || ''
      const homeAway = competitor.homeAway
      const linescores = competitor.linescores || []
      linescores.map(linescore => {

        let totalRuns = 0
        const overs = selectn('statistics.overs[0]', linescore) || []
        const period = linescore.period

        parsed[homeAway] = parsed[homeAway] || {}
        parsed[homeAway][period] = {
          name: homeAway,
          teamName: teamName,
          teamAbbr: teamAbbr,
          values: []
        }

        overs.map((over = {}) => {
          //console.info('over', over)
          let data = {}
          let runs = Number(over.runs)
          totalRuns = totalRuns + runs

          let overNumber = Number(over.number)
          const currentRate = Number((totalRuns/overNumber).toFixed(2))

          data.over = overNumber
          data.runs = runs
          data.totalRuns = totalRuns
          data.rate = currentRate
          data.wickets = (over.wicket || []).map((wicket = {}) => {
            //console.info('wicket', wicket)
            // Default text to show in tooltip
            let finalText = ''
            // Append all wicket related to details to this array
            let allDetails = []
            /**
             * Default strings for over number, comment, how out + batsman stats
             * , strike rate and FOW
             */
            let overNumString = ''
            let commentString = ''
            let wicketTextString = ''
            let srString = ''
            let fowString = ''
            let wicketStats = ''
            let shortText = selectn('details.shortText', wicket) || ''
            let wicketOver = wicket.over || ''
            //console.info('wicket.over', wicket.over)
            // Get ove number
            overNumString = `<strong>${wicketOver} ${shortText} </strong>`
            // Get actual comment for wicket
            let comment = selectn('details.text', wicket)
            if (typeof comment !== 'undefined') {
              commentString = comment
            }
            // Get strike rate of fallen batsman
            if (wicket.hasOwnProperty('strikeRate')) {
              srString = `<strong>SR: ${wicket.strikeRate}</strong>`
            }
            // Get FOW
            if (wicket.hasOwnProperty('fow')) {
              fowString = `Fall of wicket: ${wicket.fow}`
            }
            wicketStats = helpers.getWicketStats(wicket)
            // Prep textDetails array - order should be as per design
            let textDetails = [ overNumString, commentString, wicketTextString,wicketStats, srString, fowString ]
            // Loop over all details & append to allDetails if not empty string
            for (var i = 0; i < textDetails.length; i++) {
              let detailInfo = textDetails[i]
              if (detailInfo !== '') {
                allDetails.push(detailInfo)
              }
            }
            // Construct finalText only if at least one detail is present
            if (allDetails.length > 0) {
              finalText = allDetails.join('<br/>')
            }
            return {
              text : finalText
            }
          })

          parsed[homeAway][period].values.push(data)
        })
      })
    })

    if(selectn('home[1].values.length', parsed) || selectn('away[2].values.length', parsed)) {
      comparison.push([ parsed.home[1], parsed.away[2] ])
    }

    if(selectn('home[2].values.length', parsed) || selectn('away[1].values.length', parsed)) {
      comparison.push([ parsed.home[2], parsed.away[1] ])
    }

    return { comparison }
  },
  getNews(newsNode = {}, edition = {}, params = {}, query = {}, hrefConfig) {
    const items = []
    const count = 5
    let articles = newsNode.articles || []
    let news = {}
    if (articles['constructor'] !== Array) {
      articles = []
    }
    articles.map(article => {
      const { headline, type = '' } = article
      let image = selectn('images[0]', article) || {}
      let video = selectn('video[0]', article) || {}

      // skip to next iteration if headline invalid
      if (typeof headline !== 'string' || headline === '') {
        return
      }

      image.url = common.getCombinerImage( image.url )
      video.thumbnail = common.getCombinerImage( video.thumbnail )

      if(items.length < count && type.toLowerCase() !== 'media') {
        items.push({
          headline: article.headline,
          description: article.description,
          href: common.buildURL(selectn('links.web.href', article), edition.domain) || '#',
          image,
          video,
          trackingName : common.createTrackingName([ params.sport, 'game', params.pageType, 'cricketnews', article.id ])
        })
      }
    })
    if (newsNode.type && newsNode.type == 'series') {
      news = {
        items,
        title: newsNode.header || ''
      }
    } else {
      news = {
        linkText: 'All Match News',
        href: common.createHref({ query, ...params, pageType: 'coverage', ...hrefConfig }),
        trackingName: common.createTrackingName([ params.sport, 'game', params.pageType, 'cricketnews', 'all' ]),
        items
      }
    }
    return news
  },
  getHawkEye(notesNode=[], device = {}) {
    let hawkEyeSrc = ''
    let show = false
    if(notesNode.length) {
      notesNode.map((n) => {
        if(n.type === 'hawkeye' && n.href) {
          hawkEyeSrc = n.href
          if(hawkEyeSrc.indexOf('http:') !== -1) {
            hawkEyeSrc = hawkEyeSrc.replace('http:', 'https:')
          }
        }
      })
    }
    if((device.desktop == true || device.tablet == true) && hawkEyeSrc.length > 0) {
      show = true
    }
    return { hawkEyeSrc, show }
  }

}

export default modules



// WEBPACK FOOTER //
// ../app/lib/transform/parsers/cricket/gamepackage/modules.js